<script>
import { ElementComponent } from 'clockvine-vue';
import numeral from 'numeral';
import ElementOperations from './helpers/ElementOperations';

export default {
    mixins: [
        ElementComponent.for('orders'),
        ElementOperations
    ],

    data: () => ({
        zone: null
    }),

    computed: {
        me() {
            return JSON.parse(this.$root.$el.attributes['data-user'].value);
        },

        isAdministrator() {
            let user = JSON.parse(this.$root.$el.attributes['data-user'].value);
            return user.role == 'Administrator';
        },

        isDealer() {
            return this.$parent.isDealer;
        },

        oilSubTotal() {
            if (parseFloat(this.element.gallons_delivered)) {
                return numeral(this.element.price).multiply(this.element.gallons_delivered).value();
            } else if (parseFloat(this.element.gallons)) {
                return numeral(this.element.price).multiply(this.element.gallons).value();
            } else if (this.element.oilSubtotal) {
                return this.element.oilSubtotal;
            } else {
                return 0;
            }
        },

        emergencyAvailable() {
            return this.zone && this.zone.emergency_delivery_available && this.zone.emergency_delivery_fee > 0;
        },

        emergencyAvailableToolTip() {
            return !this.emergencyAvailable ? 'Emergency delivery fee unavailable or 0' : null;
        },

        processingFeeAvailable() {
            return this.zone && this.zone.processing_fee > 0;
        },

        processingFeeToolTip() {
            return !this.processingFeeAvailable ? 'Processing fee unavailable or 0' : null;
        },

        orderFeeApplicable() {
            let gallons = this.element.gallons_delivered || this.element.gallons;
            let zone = this.element.zip_code.zone ?? this.zone;
            if (gallons < 100 && zone.small_order_fee > 0) {
                return true;
            } else if (gallons < 150 && zone.medium_order_fee > 0) {
                return true;
            } else {
                return false;
            }
        },

        orderFeeToolTip() {
            return !this.orderFeeApplicable ? 'Small order fee unavailable or 0' : null;
        },


        cc_fee() {
            return Math.round(
                numeral(this.element.credit_card_fee_rate).value() * (this.oilSubTotal + this.fee_total)
            ) / 100;
        },

        fee_total() {
            return [
                this.element.emergency_fee,
                this.element.small_order_fee,
                this.element.prime_fee,
                this.element.additional_fee,
            ].map(parseFloat)
             .reduce((a, b) => a + b, 0)
        },

        tax() {
            return Math.round(this.oilSubTotal * this.element.tax_rate) / 100;
        },

        total() {
            return this.oilSubTotal + this.fee_total + this.tax;
        },

        changes() {
            let changes = [];
            if (this.element.emergency_fee > 0 && !this.element.started_as_emergency) {
                changes.push('An emergency fee was added to this order.');
            } else if (this.element.emergency_fee == 0 && this.element.started_as_emergency) {
                changes.push('An emergency fee was waived from this order.');
            }

            if (this.element.gallons_delivered && this.element.small_order_fee == 0 && this.element.gallons_delivered < 150) {
                changes.push('The small order fee was waived from this order.');
            } else if (!this.element.gallons_delivered && this.element.small_order_fee == 0 && this.element.gallons < 150) {
                changes.push('The small order fee was waived from this order.');
            }

            if (this.element.prime_fee > 0 && !this.element.started_needing_prime) {
                changes.push('A prime fee was added to this order.');
            } else if (this.element.prime_fee == 0 && this.element.started_needing_prime) {
                changes.push('A prime fee was waived from this order.');
            }

            return changes;
        },
    },

    methods: {
        baseRecord: () => ({}),

        toggleProcessingFee() {
            this.element.processing_fee = this.element.processing_fee ? 0 : this.zone.processing_fee;
        },

        toggleEmergencyFee() {
            this.element.emergency_fee = this.element.emergency_fee ? 0 : this.zone.emergency_delivery_fee;
        },

        togglePrimeFee() {
            this.element.prime_fee = this.element.prime_fee ? 0 : this.zone.prime_fee;
        },

        toggleSmallOrderFee() {
            if (this.element.small_order_fee) {
                this.element.small_order_fee = 0;
            } else {
                this.updateSmallOrderFee();
            }
        },

        updateSmallOrderFee() {
            let gallons = this.element.gallons_delivered || this.element.gallons;
            if (gallons < 100) {
                this.element.small_order_fee = this.zone.small_order_fee;
            } else if (gallons < 150) {
                this.element.small_order_fee = this.zone.medium_order_fee;
            } else {
                this.element.small_order_fee = 0;
            }
        },

        checkOverLimit() {
            if (!this.isAdministrator && this.element.gallons_delivered > this.element.gallons) {
                this.element.gallons_delivered = this.element.gallons;
            }
        },
    },

    watch: {
        element: {
            deep: true,
            handler(newVal, oldVal) {
                if (oldVal === undefined || oldVal === null || Object.keys(oldVal).length == 0) {
                    return;
                }
                if (this.unchangedRecord === null) {
                    this.unchangedRecord = JSON.parse(JSON.stringify(newVal));
                }
                if (newVal.dealer_zone_id) {
                    this.$store.dispatch('dealerZones/show', {id: newVal.dealer_zone_id}).then(data => {
                        this.element.zip_code.zone = data.data.data;
                        this.zone = data.data.data;
                    })
                }
                if (!this.isLoading && this.changed) {
                    this.$emit(
                        this.hasChanges ? 'pending' : 'clear',
                        this.element
                    );
                }
            }
        },
        "element.dealer_zone_id"(newVal, oldVal) {
            const dealer_zone_id = (newVal == undefined) ? oldVal : newVal;
            if (dealer_zone_id) {
                this.$store.dispatch('dealerZones/show', {id: dealer_zone_id}).then(data => {
                    this.element.zip_code.zone = data.data.data;
                    this.zone = data.data.data;
                })
            }
        },
        "element.gallons"(newVal, oldVal) {
            if (oldVal === undefined) {
                return;
            }
            this.updateSmallOrderFee();
        },
        "element.gallons_delivered"(newVal, oldVal) {
            if (oldVal === undefined) {
                return;
            }

            if (newVal && !oldVal && (this.element.status == 'New' || this.element.status == 'Processing')) {
                this.element.status = 'Delivered';
            } else if (oldVal && !newVal && (this.element.status == 'Delivered')) {
                this.element.status = 'Processing';
                this.element.delivered_at = null;
            }

            this.updateSmallOrderFee();
        },
    },
}
</script>
